import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  User, Phone, Lock, LogOut, Loader2, ArrowLeft, ShieldCheck,
  Calendar, MapPin, Ticket, IndianRupee, Download, MessageCircle,
  Map, UtensilsCrossed, Youtube, ExternalLink
} from "lucide-react";
import { openWhatsApp } from "./lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Same fallback venue images used on the Locations page (kept in sync)
const VENUE_IMAGES = [
  "https://images.unsplash.com/photo-1542204165-65bf26472b9b?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1517604931442-7e0c8ed2963c?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1524985069026-dd778a71c7b4?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1598899134739-24c46f58b8c0?auto=format&fit=crop&w=1200&q=80",
];
const AUTH_KEY = "cv_user_auth";

// Extract a YouTube video id from various URL shapes and return a thumbnail
const ytThumb = (url) => {
  if (!url) return "";
  const m = String(url).match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : "";
};

const Input = (p) => (
  <input
    {...p}
    className={`w-full bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2.5 text-sm text-white placeholder:text-[#7a6c54] focus:outline-none focus:border-[#d4a574] ${p.className || ""}`}
  />
);

const Btn = ({ children, className = "", disabled, ...p }) => (
  <button
    {...p}
    disabled={disabled}
    className={`bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold tracking-wide px-5 py-2.5 rounded-md hover:from-[#f1d59c] hover:to-[#d4ac6f] transition disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
  >
    {children}
  </button>
);

const StatusPill = ({ status }) => {
  const map = {
    confirmed: "bg-[#143c20] text-[#7ad99c] border-[#7ad99c]/30",
    pending: "bg-[#3a2a14] text-[#f4d089] border-[#f4d089]/30",
    cancelled: "bg-[#3a1414] text-[#f48989] border-[#f48989]/30",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${map[status] || map.pending}`}>
      {status || "—"}
    </span>
  );
};

export default function Account() {
  const nav = useNavigate();
  const [auth, setAuth] = useState(() => {
    try { return JSON.parse(localStorage.getItem(AUTH_KEY) || "null"); } catch { return null; }
  });

  // login/signup form
  const [mode, setMode] = useState("login"); // login | signup
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // bookings
  const [bookings, setBookings] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingTab, setBookingTab] = useState("upcoming"); // upcoming | past
  const [payCfg, setPayCfg] = useState({ enabled: false, key_id: "" });
  const [payingId, setPayingId] = useState("");
  const [payErr, setPayErr] = useState("");

  const persistAuth = (a) => {
    setAuth(a);
    localStorage.setItem(AUTH_KEY, JSON.stringify(a));
  };

  const logout = () => {
    setAuth(null);
    localStorage.removeItem(AUTH_KEY);
    setBookings([]);
  };

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const ph = phone.replace(/\D/g, "");
      if (ph.length !== 10) throw new Error("Enter a valid 10-digit mobile number");
      if (!password || password.length < 6) throw new Error("Password must be at least 6 characters");
      const url = mode === "signup" ? `${API}/auth/register` : `${API}/auth/login`;
      const payload = mode === "signup"
        ? { phone: ph, password, name: name.trim(), email: email.trim() }
        : { phone: ph, password };
      const r = await axios.post(url, payload);
      persistAuth(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Something went wrong");
    } finally { setBusy(false); }
  };

  const fetchBookings = async () => {
    if (!auth?.token) return;
    setLoadingBookings(true);
    try {
      const r = await axios.get(`${API}/auth/me/bookings`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      setBookings(r.data.bookings || []);
    } catch (e) {
      if (e.response?.status === 401) logout();
    } finally { setLoadingBookings(false); }
  };

  useEffect(() => { if (auth) fetchBookings(); /* eslint-disable-next-line */ }, [auth?.token]);
  // Split bookings into upcoming & past based on date (today included = upcoming)
  const todayStr = new Date().toISOString().slice(0, 10);
  const upcomingBookings = bookings.filter(
    (b) => (b.date || "") >= todayStr && b.status !== "cancelled"
  );
  const pastBookings = bookings.filter(
    (b) => (b.date || "") < todayStr || b.status === "cancelled"
  );
  const visibleBookings = bookingTab === "upcoming" ? upcomingBookings : pastBookings;
  useEffect(() => { axios.get(`${API}/payments/config`).then((r) => setPayCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => { axios.get(`${API}/branches`).then((r) => setBranches(r.data || [])).catch(() => {}); }, []);

  const loadRazorpayScript = () => new Promise((resolve, reject) => {
    if (typeof window.Razorpay === "function") return resolve();
    const existing = document.getElementById("razorpay-checkout-script");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Payment gateway failed to load.")));
      return;
    }
    const s = document.createElement("script");
    s.id = "razorpay-checkout-script";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Payment gateway failed to load."));
    document.body.appendChild(s);
  });

  const payBalance = async (b) => {
    setPayErr(""); setPayingId(b.id);
    try {
      const headers = { Authorization: `Bearer ${auth.token}` };
      // Mock mode – no Razorpay configured; just mark balance as paid
      if (!payCfg.enabled) {
        await axios.post(`${API}/auth/me/bookings/${b.id}/mark-balance-mock`, {}, { headers });
        await fetchBookings();
        return;
      }
      const orderRes = await axios.post(`${API}/auth/me/bookings/${b.id}/balance-order`, {}, { headers });
      const order = orderRes.data;
      await loadRazorpayScript();
      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          name: "The CineVibe · Balance",
          description: `Balance · ${b.invoice_no || b.id.slice(0, 8)}`,
          order_id: order.order_id,
          prefill: { name: auth.user.name || "", contact: auth.user.phone || "" },
          theme: { color: "#d4a574" },
          handler: async (res) => {
            try {
              await axios.post(`${API}/auth/me/bookings/${b.id}/balance-verify`, {
                booking_id: b.id,
                razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
              }, { headers });
              await fetchBookings();
              resolve();
            } catch (e) {
              reject(new Error(e.response?.data?.detail || "Payment verification failed"));
            }
          },
          modal: { ondismiss: () => reject(new Error("Payment cancelled")) },
        });
        rzp.on("payment.failed", (resp) => reject(new Error(resp?.error?.description || "Payment failed")));
        rzp.open();
      });
    } catch (e) {
      setPayErr(e.response?.data?.detail || e.message || "Could not process payment");
    } finally {
      setPayingId("");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0604] text-white" data-testid="account-page">
      <header className="border-b border-[#3a2a14]/60 bg-[#0a0604]/85 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1100px] mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#bfb393] hover:text-[#f4d089] text-sm" data-testid="account-back-home">
            <ArrowLeft size={14} /> Back to Home
          </Link>
          <div className="font-display text-[#f4d089]">The CineVibe · My Account</div>
          <div className="w-20" />
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-5 py-10">
        {!auth ? (
          <div className="max-w-md mx-auto rounded-lg border border-[#3a2a14] bg-[#0d0905] p-6" data-testid="auth-card">
            <h1 className="font-display text-3xl text-white mb-1">{mode === "signup" ? "Create your account" : "Welcome back"}</h1>
            <p className="text-sm text-[#a89878] mb-5">
              {mode === "signup"
                ? "Sign up with your mobile number to track your bookings."
                : "Login with your mobile number and password."}
            </p>

            <div className="flex gap-2 mb-5 text-xs">
              <button onClick={() => { setMode("login"); setErr(""); }} data-testid="tab-login"
                className={`px-3 py-1.5 rounded-full border ${mode === "login" ? "bg-[#3a2a14] text-[#f4d089] border-[#f4d089]/40" : "border-[#3a2a14] text-[#a89878]"}`}>
                Log in
              </button>
              <button onClick={() => { setMode("signup"); setErr(""); }} data-testid="tab-signup"
                className={`px-3 py-1.5 rounded-full border ${mode === "signup" ? "bg-[#3a2a14] text-[#f4d089] border-[#f4d089]/40" : "border-[#3a2a14] text-[#a89878]"}`}>
                Sign up
              </button>
            </div>

            {err && <div className="text-xs text-red-400 mb-3 border border-red-500/30 bg-red-500/10 rounded px-3 py-2" data-testid="auth-error">{err}</div>}

            <div className="space-y-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><Phone size={12} /> Mobile Number</div>
                <Input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="10-digit mobile" inputMode="numeric" maxLength={10} data-testid="login-phone" />
              </div>
              {mode === "signup" && (
                <>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><User size={12} /> Full Name (optional)</div>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Aman Singh" data-testid="signup-name" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2">Email (optional)</div>
                    <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="signup-email" />
                  </div>
                </>
              )}
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><Lock size={12} /> Password</div>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters" data-testid="login-password" />
              </div>
              <Btn onClick={submit} disabled={busy} className="w-full mt-2" data-testid="login-submit">
                {busy ? <><Loader2 size={14} className="inline mr-1 animate-spin" /> Please wait…</> : (mode === "signup" ? "Sign up" : "Log in")}
              </Btn>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between rounded-lg border border-[#7ad99c]/40 bg-[#0d1a10] p-4 mb-6" data-testid="account-banner">
              <div className="flex items-center gap-3">
                <ShieldCheck size={18} className="text-[#7ad99c]" />
                <div>
                  <div className="text-white font-semibold">{auth.user.name || "Welcome"}</div>
                  <div className="text-xs text-[#a89878]">+91 {auth.user.phone}{auth.user.email ? ` · ${auth.user.email}` : ""}</div>
                </div>
              </div>
              <button onClick={logout} className="text-xs text-[#a89878] hover:text-[#f4d089] flex items-center gap-1" data-testid="account-logout">
                <LogOut size={12} /> Log out
              </button>
            </div>

            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl text-white">My Bookings</h2>
              <Link to="/book" className="text-sm text-[#f4d089] hover:underline" data-testid="new-booking-link">+ New booking</Link>
            </div>

            {/* Upcoming / Past tabs */}
            <div className="flex items-center gap-2 mb-4 border-b border-[#3a2a14]" data-testid="bookings-tabs">
              <button
                onClick={() => setBookingTab("upcoming")}
                data-testid="tab-upcoming"
                className={`px-4 py-2 text-sm tracking-wide -mb-px border-b-2 transition ${
                  bookingTab === "upcoming"
                    ? "border-[#f4d089] text-[#f4d089]"
                    : "border-transparent text-[#a89878] hover:text-white"
                }`}
              >
                Upcoming ({upcomingBookings.length})
              </button>
              <button
                onClick={() => setBookingTab("past")}
                data-testid="tab-past"
                className={`px-4 py-2 text-sm tracking-wide -mb-px border-b-2 transition ${
                  bookingTab === "past"
                    ? "border-[#f4d089] text-[#f4d089]"
                    : "border-transparent text-[#a89878] hover:text-white"
                }`}
              >
                Past ({pastBookings.length})
              </button>
            </div>

            {payErr && (
              <div className="text-xs text-red-400 mb-3 border border-red-500/30 bg-red-500/10 rounded px-3 py-2" data-testid="pay-error">{payErr}</div>
            )}

            {loadingBookings ? (
              <div className="text-sm text-[#a89878] py-10 text-center"><Loader2 size={16} className="inline mr-2 animate-spin" /> Loading your bookings…</div>
            ) : visibleBookings.length === 0 ? (
              <div className="text-sm text-[#a89878] border border-[#3a2a14] rounded-md p-8 text-center" data-testid="no-bookings">
                {bookingTab === "upcoming" ? (
                  <>No upcoming bookings. <Link to="/book" className="text-[#f4d089] underline">Book your next screening →</Link></>
                ) : (
                  <>No past bookings yet.</>
                )}
              </div>
            ) : (
              <div className="grid gap-3" data-testid="bookings-list">
                {visibleBookings.map((b) => (
                  <div
                    key={b.id}
                    className="rounded-lg border border-[#3a2a14] bg-[#0d0905] p-4 flex flex-col gap-4 sm:flex-row sm:items-stretch"
                    data-testid={`booking-${b.id}`}
                  >
                    {/* Show the same room/theatre image the customer saw while booking */}
                    {(() => {
                      const venueImg =
                        b.room_image_url ||
                        (b.room_images && b.room_images[0]) ||
                        b.branch_image_url ||
                        "";
                      return venueImg ? (
                        <img
                          src={venueImg}
                          alt={b.room_name || b.branch_name || "CineVibe theatre"}
                          className="w-full sm:w-44 h-32 sm:h-auto object-cover rounded-md border border-[#3a2a14]"
                          data-testid={`booking-image-${b.id}`}
                        />
                      ) : (
                        <div className="w-full sm:w-44 h-32 sm:h-auto rounded-md border border-[#3a2a14] bg-[#1a0f06] flex items-center justify-center text-[#a89878] text-xs">
                          The CineVibe
                        </div>
                      );
                    })()}

                    {/* Middle: details */}
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display text-lg text-[#f4d089]">{b.invoice_no || b.id.slice(0, 8)}</span>
                        <StatusPill status={b.status} />
                        {b.payment_status && b.payment_status !== "unpaid" && b.payment_status !== "mock" && (
                          <span className="text-[10px] text-[#7ad99c] border border-[#7ad99c]/30 px-2 py-0.5 rounded uppercase">
                            {b.payment_status}
                          </span>
                        )}
                        {Number(b.balance_due || 0) > 0 && (
                          <span className="text-[10px] text-[#f4d089] border border-[#f4d089]/40 px-2 py-0.5 rounded uppercase" data-testid={`balance-pill-${b.id}`}>
                            Balance ₹{b.balance_due}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-xs text-[#bfb393] flex-wrap">
                        <span className="flex items-center gap-1"><MapPin size={12} /> {b.branch_name || "—"}</span>
                        <span className="flex items-center gap-1"><Ticket size={12} /> {b.room_name || "—"}</span>
                        <span className="flex items-center gap-1"><Calendar size={12} /> {b.date} · {(b.slots && b.slots.length ? b.slots.join(", ") : b.slot)}</span>
                        <span>· {b.guests} guests</span>
                      </div>

                      {b.branch_address && (
                        <div className="text-[11px] text-[#a89878] flex items-start gap-1">
                          <MapPin size={11} className="mt-0.5 shrink-0" /> {b.branch_address}
                        </div>
                      )}

                      {/* Quick links: Map / Menu / YouTube */}
                      {(b.branch_map_link || b.branch_menu_url || b.branch_video_link) && (
                        <div className="flex flex-wrap items-center gap-2 pt-1" data-testid={`booking-links-${b.id}`}>
                          {b.branch_map_link && (
                            <a
                              href={b.branch_map_link}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`map-link-${b.id}`}
                              className="text-[11px] flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#3a2a14] text-[#bfb393] hover:text-[#f4d089] hover:border-[#f4d089]/40"
                            >
                              <Map size={12} /> Map <ExternalLink size={10} />
                            </a>
                          )}
                          {b.branch_menu_url && (
                            <a
                              href={b.branch_menu_url}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`menu-link-${b.id}`}
                              className="text-[11px] flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#3a2a14] text-[#bfb393] hover:text-[#f4d089] hover:border-[#f4d089]/40"
                            >
                              <UtensilsCrossed size={12} /> Menu <ExternalLink size={10} />
                            </a>
                          )}
                          {b.branch_video_link && (
                            <a
                              href={b.branch_video_link}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`youtube-link-${b.id}`}
                              className="text-[11px] flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#3a2a14] text-[#bfb393] hover:text-[#f4d089] hover:border-[#f4d089]/40"
                            >
                              <Youtube size={12} /> Video <ExternalLink size={10} />
                            </a>
                          )}
                        </div>
                      )}

                      {Number(b.balance_due || 0) > 0 && (
                        <div className="text-[11px] text-[#a89878]">
                          Paid ₹{b.paid_amount || b.advance_amount || 0} · Balance ₹{b.balance_due}
                        </div>
                      )}
                    </div>

                    {/* Right: total + action */}
                    <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-3 sm:gap-4 sm:min-w-[160px]">
                      <div className="text-right">
                        <div className="text-[10px] text-[#a89878] uppercase tracking-wider">Total</div>
                        <div className="font-display text-xl text-[#f4d089] flex items-center gap-1"><IndianRupee size={16} />{b.amount}</div>
                      </div>
                      {Number(b.balance_due || 0) > 0 ? (
                        <Btn
                          onClick={() => payBalance(b)}
                          disabled={payingId === b.id}
                          data-testid={`pay-balance-${b.id}`}
                          className="text-xs px-3 py-2"
                        >
                          {payingId === b.id ? (
                            <><Loader2 size={12} className="inline mr-1 animate-spin" /> Processing…</>
                          ) : (
                            <>Pay Balance ₹{b.balance_due}</>
                          )}
                        </Btn>
                      ) : (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${b.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs flex items-center gap-1 px-3 py-2 rounded-md border border-[#3a2a14] text-[#f4d089] hover:bg-[#3a2a14]/30"
                          data-testid={`download-invoice-${b.id}`}
                        >
                          <Download size={12} /> Invoice
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Floating WhatsApp support button */}
      <button
        onClick={() =>
          openWhatsApp(
            auth
              ? `Hi The CineVibe support! I need help with my account / bookings. (Phone: ${auth.phone || ""})`
              : "Hi The CineVibe support! I need some help."
          )
        }
        aria-label="Chat with The CineVibe support on WhatsApp"
        data-testid="account-whatsapp-fab"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-[#25D366] hover:bg-[#20bf5a] text-white rounded-full pl-4 pr-5 py-3 shadow-lg shadow-black/40 transition hover:scale-105"
      >
        <MessageCircle size={20} />
        <span className="hidden sm:inline text-sm font-semibold">Support</span>
      </button>
    </div>
  );
}
